import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Plus, Trash2, Upload, ShieldOff, Info } from 'lucide-react';
import { emailService, EmailSuppression } from 'api/services/email.service';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';

const REASON_STYLES: Record<string, string> = {
  UNSUBSCRIBED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  BOUNCED: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  COMPLAINED: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  MANUAL: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
};
const REASONS = ['ALL', 'UNSUBSCRIBED', 'BOUNCED', 'COMPLAINED', 'MANUAL'];
const PAGE_SIZE = 50;

/**
 * The organization's do-not-email list.
 *
 * One list per ORGANIZATION, not per sender: an org has several senders (SES and
 * SMTP side by side) and to a recipient they are all the same company, so
 * unsubscribing has to silence all of them.
 */
const EmailSuppressions: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<EmailSuppression[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [reason, setReason] = useState('ALL');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [newEmail, setNewEmail] = useState('');
  const [bulk, setBulk] = useState('');
  const [showBulk, setShowBulk] = useState(false);

  const load = useCallback(async (r = reason, p = page) => {
    const [list, c] = await Promise.all([
      emailService.listSuppressions({ reason: r === 'ALL' ? undefined : r, page: p, size: PAGE_SIZE }),
      emailService.suppressionCounts(),
    ]);
    if (list.success && list.data) {
      setRows(list.data.content || []);
      setTotalPages(list.data.totalPages || 0);
      setTotal(list.data.totalElements || 0);
      setError(null);
    } else setError(list.message || 'Failed to load the list');
    if (c.success) setCounts(c.data || {});
    setLoading(false);
  }, [reason, page]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!newEmail.trim()) return;
    const res = await emailService.addSuppression(newEmail.trim());
    if (!res.success) { setError(res.message || 'Failed to add'); return; }
    setNotice(res.data?.alreadySuppressed ? `${res.data.email} was already on the list` : `${res.data?.email} added`);
    setNewEmail('');
    load();
  };

  const importList = async () => {
    const emails = bulk.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    if (emails.length === 0) return;
    const res = await emailService.importSuppressions(emails);
    if (!res.success) { setError(res.message || 'Import failed'); return; }
    // Reporting duplicates separately: an import that says "5,000 added" when
    // 4,900 were already there reads as a list five times the size it is.
    setNotice(`${res.data?.added} added, ${res.data?.alreadySuppressed} already on the list`);
    setBulk('');
    setShowBulk(false);
    load();
  };

  const remove = async (email: string) => {
    if (!window.confirm(`Remove ${email} from the do-not-email list? They will start receiving campaigns again.`)) return;
    const res = await emailService.removeSuppression(email);
    if (!res.success) { setError(res.message || 'Failed to remove'); return; }
    setNotice(`${email} removed`);
    load();
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <button onClick={() => navigate('/email/campaigns')}
        className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-indigo-600">
        <ArrowLeft className="w-4 h-4" /> Back to campaigns
      </button>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <ShieldOff className="w-7 h-7 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Do not email</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              People this organization will not send campaigns to.
            </p>
          </div>
        </div>
        <button onClick={() => load()} title="Refresh"
          className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <p className="flex items-start gap-2 text-[11px] text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60 rounded-lg p-2.5">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        This list covers the whole organization, not one sender. You have several senders and
        to a recipient they are all the same company, so an unsubscribe silences all of them.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Tile label="Total" value={counts.total ?? 0} tone="text-gray-700 dark:text-gray-200" />
        <Tile label="Unsubscribed" value={counts.unsubscribed ?? 0} tone="text-blue-600" />
        <Tile label="Bounced" value={counts.bounced ?? 0} tone="text-red-600" />
        <Tile label="Complained" value={counts.complained ?? 0} tone="text-orange-500" />
        <Tile label="Added by hand" value={counts.manual ?? 0} tone="text-gray-600 dark:text-gray-300" />
      </div>

      {error && <ErrorMessage message={error} />}
      {notice && <div className="text-sm text-green-700 bg-green-50 dark:bg-green-900/20 rounded-lg p-3">{notice}</div>}

      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[16rem]">
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Add an address</label>
          <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
            placeholder="someone@example.com"
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200" />
        </div>
        <button onClick={add}
          className="tl-btn tl-btn-primary tl-btn-sm">
          <Plus className="w-4 h-4" /> Add
        </button>
        <button onClick={() => setShowBulk((v) => !v)}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200">
          <Upload className="w-4 h-4" /> Import a list
        </button>
        <select value={reason} onChange={(e) => { setReason(e.target.value); setPage(0); load(e.target.value, 0); }}
          className="px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200">
          {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      {showBulk && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
          <label className="block text-sm text-gray-700 dark:text-gray-200">
            Paste addresses — one per line, or separated by commas
          </label>
          <textarea value={bulk} onChange={(e) => setBulk(e.target.value)} rows={6}
            className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200" />
          <div className="flex gap-2">
            <button onClick={importList}
              className="tl-btn tl-btn-primary tl-btn-sm">Import</button>
            <button onClick={() => setShowBulk(false)}
              className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <LoadingSpinner /> : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 font-medium text-gray-700 dark:text-gray-200">
            {total} address{total === 1 ? '' : 'es'}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium">Email</th>
                  <th className="px-4 py-2 font-medium">Reason</th>
                  <th className="px-4 py-2 font-medium">Detail</th>
                  <th className="px-4 py-2 font-medium">Added</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-2 text-gray-800 dark:text-gray-200">{r.email}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${REASON_STYLES[r.reason] || ''}`}>
                        {r.reason}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 max-w-md truncate" title={r.notes || ''}>
                      {r.notes || ''}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">
                      {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ''}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button title="Remove" onClick={() => remove(r.email)}
                        className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                    Nobody on this list{reason !== 'ALL' ? ` for ${reason}` : ''}.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-sm">
              <button disabled={page === 0} onClick={() => { const p = page - 1; setPage(p); load(reason, p); }}
                className="px-2 py-1 text-gray-600 dark:text-gray-300 disabled:opacity-40">Previous</button>
              <span className="text-gray-500 dark:text-gray-400">Page {page + 1} of {totalPages}</span>
              <button disabled={page + 1 >= totalPages} onClick={() => { const p = page + 1; setPage(p); load(reason, p); }}
                className="px-2 py-1 text-gray-600 dark:text-gray-300 disabled:opacity-40">Next</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Tile: React.FC<{ label: string; value: number; tone: string }> = ({ label, value, tone }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
    <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
    <div className={`text-2xl font-semibold mt-1 ${tone}`}>{value}</div>
  </div>
);

export default EmailSuppressions;
